import React, { useEffect, useMemo, useState } from 'react';
import { StatsigProvider as BaseStatsigProvider, useStatsigClient } from '@statsig/react-bindings';
import { StatsigTriggeredSessionReplayPlugin } from '@statsig/session-replay';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';

import { APP_DEPLOY_VERSION } from '../../config/appVersion';
import { setNotificationStatsigClient } from '../../features/system-notifications/services/trackNotificationError';

import { sanitizeMetadata } from './sanitizer';
import { setStatsigClientInstance } from './trackNoteTelemetryToStatsig';

// Type for LocalOverrideAdapter (imported dynamically)
type LocalOverrideAdapter = any;

interface StatsigProviderProps {
  children: React.ReactNode;
}

// Stable user object to prevent re-initialization
const ANONYMOUS_USER = {
  appVersion: APP_DEPLOY_VERSION,
};

/**
 * Inner component that initializes the Statsig client for non-React contexts
 * Must be rendered inside BaseStatsigProvider to access useStatsigClient
 */
const StatsigClientInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { client } = useStatsigClient();

  useEffect(() => {
    // Initialize the client instance for use in non-React contexts (like NoteTelemetry)
    setStatsigClientInstance(client);
    // Also initialize for system notification error tracking
    setNotificationStatsigClient(client);

    if (import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'dev') {
      console.debug('[Statsig] Client instance initialized for NoteTelemetry tracking');
    }
  }, [client]);

  return <>{children}</>;
};

export const StatsigProviderWrapper: React.FC<StatsigProviderProps> = ({ children }) => {
  const sdkKey = import.meta.env.VITE_STATSIG_SDK_KEY as string;
  const [overrideAdapter, setOverrideAdapter] = useState<LocalOverrideAdapter | undefined>(
    undefined,
  );

  // Memoize plugins to prevent recreation on every render
  const plugins = useMemo(
    () => [
      new StatsigTriggeredSessionReplayPlugin({
        autoStartRecording: false,
        keepRollingWindow: true,
        rrwebConfig: {
          blockClass: 'rr-block',
          maskTextClass: 'rr-mask',
          sampling: {
            scroll: 50,
            input: 'last',
          },
        },
      }),
      new StatsigAutoCapturePlugin({
        eventFilterFunc: event => {
          if (event.metadata) {
            event.metadata = sanitizeMetadata(event.metadata);
          }
          return true;
        },
      }),
    ],
    [],
  );

  // Memoize options to prevent re-initialization
  const options = useMemo(
    () => ({
      environment: {
        tier: import.meta.env.VITE_BUILD_ENV === 'alpha' ? 'staging' : 'production',
      },
      overrideAdapter,
      plugins,
    }),
    [overrideAdapter, plugins],
  );

  // Load override adapter dynamically in dev mode or E2E test mode
  useEffect(() => {
    if ((import.meta.env.DEV || import.meta.env.VITE_E2E_MODE) && sdkKey) {
      import('@statsig/js-local-overrides')
        .then(module => {
          const adapter = new module.LocalOverrideAdapter(sdkKey);
          setOverrideAdapter(adapter);

          // Expose to window for console access in dev/E2E mode
          (window as any).statsigOverrides = adapter;
          (window as any).statsigDebug = {
            showOverrides: () => adapter.getAllOverrides(),
            showStorage: () => {
              const keys = Object.keys(localStorage).filter(k => k.includes('local-overrides'));
              keys.forEach(k => console.log(k, localStorage.getItem(k)));
            },
          };
        })
        .catch(error => {
          console.warn('Failed to load @statsig/js-local-overrides:', error);
        });
    }
  }, [sdkKey]);

  // Don't initialize if no SDK key
  if (!sdkKey) {
    console.warn('Statsig SDK key not found. Analytics will be disabled.');
    return <>{children}</>;
  }

  return (
    <BaseStatsigProvider
      sdkKey={sdkKey}
      user={ANONYMOUS_USER}
      options={options}
    >
      <StatsigClientInitializer>{children}</StatsigClientInitializer>
    </BaseStatsigProvider>
  );
};
